import { DatePicker } from "@/components/ui/date-picker";
import PaymentInfo from "./PaymentInfo";
import { API_BASE } from "@/lib/api";

// A4 invoice layout shared by the modal (editable draft) and the PDF/print render.
export const PAYMENT_OPTIONS = ["Gotovina", "Kartica", "Transakcijski račun", ...["Booking.com", "Airbnb", "Expedia", "Agoda", "Vrbo"].map((o) => `Transakcijski račun (${o})`)];

const TITLES = {
  racun: "RAČUN / INVOICE",
  avans: "RAČUN ZA PREDUJAM / ADVANCE INVOICE",
  ponuda: "PONUDA / OFFER",
  storno: "STORNO RAČUN / CANCELLATION",
};

const ACCENT = "var(--inv-accent)";
const money = (v, cur = "EUR") => `${Number(v || 0).toLocaleString("hr-HR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;
export const fmtHrDate = (iso) => (iso && iso.length >= 10 ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}.` : iso || "—");

function Lbl({ children }) {
  return <span style={{ color: "#64748b", fontSize: 9, letterSpacing: "0.06em", textTransform: "uppercase", fontWeight: 600 }}>{children}</span>;
}

function SupplierBlock({ s = {} }) {
  const line = (v) => (v ? <div>{v}</div> : null);
  return (
    <div style={{ textAlign: "right", fontSize: 10, lineHeight: 1.55, color: "#334155" }} data-testid="invoice-supplier">
      <div style={{ fontSize: 13, fontWeight: 700, color: "#0f172a" }}>{s.company_name || s.private_landlord_name || s.display_name || "—"}</div>
      {s.display_name && s.display_name !== (s.company_name || s.private_landlord_name) && <div style={{ fontWeight: 600 }}>{s.display_name}</div>}
      {s.issuer_type !== "company" && s.private_landlord_name && <div style={{ fontStyle: "italic" }}>Privatni iznajmljivač</div>}
      {line([s.address, [s.postal_code, s.city].filter(Boolean).join(" ")].filter(Boolean).join(", "))}
      {line(s.phone && `Tel: ${s.phone}`)}
      {line(s.email)}
      {line(s.website)}
      {line(s.iban && `IBAN: ${s.iban}`)}
      {line([s.swift && `SWIFT: ${s.swift}`, s.bank_name].filter(Boolean).join(" · "))}
      {line(s.oib && `OIB: ${s.oib}`)}
      {line(s.vat_id && `VAT ID: ${s.vat_id}`)}
    </div>
  );
}

// Same-origin PNG proxy — avoids CORS-tainted canvases and SVG <filter>
// issues when html2canvas rasterises the invoice for PDF export.
const logoSrc = (url) => (url ? `${API_BASE}/invoice-logo.png?src=${encodeURIComponent(url)}` : null);

function Header({ inv }) {
  const s = inv.supplier || {};
  return (
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 28 }}>
      <div style={{ minHeight: 56 }}>
        {s.logo_url ? (
          <img src={logoSrc(s.logo_url)} alt="logo" onError={(e) => { e.currentTarget.style.display = "none"; }} style={{ maxHeight: 64, maxWidth: 220, objectFit: "contain" }} data-testid="invoice-logo" />
        ) : (
          <div style={{ fontSize: 18, fontWeight: 800, color: ACCENT }}>{s.display_name || ""}</div>
        )}
      </div>
      <SupplierBlock s={s} />
    </div>
  );
}

function MetaRow({ label, children, tid }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "150px 1fr", alignItems: "center", minHeight: 26, fontSize: 10.5 }} data-testid={tid}>
      <Lbl>{label}</Lbl>
      <div style={{ color: "#0f172a", fontWeight: 500 }}>{children}</div>
    </div>
  );
}

function MetaBlock({ inv, editable, onChange }) {
  const t = inv.invoice_type;
  const hidden = new Set(inv.hidden_fields || []);
  // One shared box style for every editable Meta field so date/select/text
  // inputs all line up with the same height, border and font size.
  const metaFieldCls = "h-7 rounded border border-slate-300 bg-white px-2 text-[11px] text-slate-900 shadow-none focus:border-sky-500 focus:outline-none";
  const sel = metaFieldCls;
  return (
    <div>
      <MetaRow label="Datum i vrijeme izdavanja" tid="inv-meta-issue">{fmtHrDate(inv.issue_date)} {inv.issue_time || ""}</MetaRow>
      <MetaRow label="Mjesto izdavanja" tid="inv-meta-place">{inv.place_of_issue || "—"}</MetaRow>
      {t !== "ponuda" && !hidden.has("delivery_date") && <MetaRow label="Datum isporuke" tid="inv-meta-delivery">{fmtHrDate(inv.delivery_date)}</MetaRow>}
      {t !== "ponuda" ? (!hidden.has("due_date") && (
        <MetaRow label="Datum dospijeća" tid="inv-meta-due">
          {editable ? <DatePicker value={inv.due_date || ""} onChange={(v) => onChange({ due_date: v })} clearable={false} className={`${metaFieldCls} w-44 hover:bg-white`} data-testid="inv-due-date" /> : fmtHrDate(inv.due_date)}
        </MetaRow>
      )) : (
        <MetaRow label="Vrijedi do" tid="inv-meta-valid">
          {editable ? <DatePicker value={inv.valid_until || ""} onChange={(v) => onChange({ valid_until: v })} className={`${metaFieldCls} w-44 hover:bg-white`} data-testid="inv-valid-until" /> : fmtHrDate(inv.valid_until)}
        </MetaRow>
      )}
      {t !== "ponuda" && !hidden.has("payment_type") && (
        <MetaRow label="Način plaćanja" tid="inv-meta-payment">
          {editable ? (
            <select value={inv.payment_type || "Gotovina"} onChange={(e) => onChange({ payment_type: e.target.value })} className={`${sel} w-44`} data-testid="inv-payment-type">
              {[...new Set([...PAYMENT_OPTIONS, inv.payment_type].filter(Boolean))].map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          ) : inv.payment_type}
        </MetaRow>
      )}
      {editable ? (
        <MetaRow label="Referenca" tid="inv-meta-ref">
          <input value={inv.client_reference_code || ""} onChange={(e) => onChange({ client_reference_code: e.target.value })} placeholder="npr. narudžbenica kupca"
            className={`${metaFieldCls} w-44`} data-testid="inv-reference-input" />
        </MetaRow>
      ) : inv.client_reference_code && <MetaRow label="Referenca" tid="inv-meta-ref">{inv.client_reference_code}</MetaRow>}
      {inv.reservation_code && <MetaRow label="ID" tid="inv-meta-id">{inv.reservation_code}</MetaRow>}
    </div>
  );
}

const CLIENT_FIELDS = [
  ["client_name", "Ime / naziv"], ["client_address", "Adresa"], ["client_postal_code", "Poštanski broj"],
  ["client_city", "Grad"], ["client_country", "Država"], ["client_vat_id", "OIB / VAT"],
];

function BuyerBlock({ inv, editable, onChange }) {
  return (
    <div style={{ textAlign: "right", fontSize: 10.5, lineHeight: 1.6 }} data-testid="invoice-buyer">
      <Lbl>Kupac / Customer</Lbl>
      {editable ? (
        <div className="mt-1 space-y-0.5">
          {CLIENT_FIELDS.map(([k, ph]) => (
            <input key={k} value={inv[k] || ""} placeholder={ph} onChange={(e) => onChange({ [k]: e.target.value })}
              className={`w-full border-0 border-b border-slate-200 bg-transparent px-1 py-0.5 text-right text-[11px] focus:border-sky-500 focus:outline-none ${k === "client_name" ? "font-semibold text-slate-900" : "text-slate-600"}`}
              data-testid={`inv-${k.replace("_", "-")}`} />
          ))}
        </div>
      ) : (
        <div style={{ color: "#334155" }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#0f172a" }}>{inv.client_name}</div>
          {inv.client_address && <div>{inv.client_address}</div>}
          {(inv.client_postal_code || inv.client_city) && <div>{[inv.client_postal_code, inv.client_city].filter(Boolean).join(" ")}</div>}
          {inv.client_country && <div>{inv.client_country}</div>}
          {inv.client_vat_id && <div>OIB: {inv.client_vat_id}</div>}
        </div>
      )}
      {inv.client_guest_name && <div style={{ marginTop: 4, color: "#475569" }} data-testid="invoice-guest-name">Ime gosta: <b>{inv.client_guest_name}</b></div>}
    </div>
  );
}

const TH = { padding: "8px 6px", fontSize: 8.5, letterSpacing: "0.05em", textTransform: "uppercase", fontWeight: 700, color: "#fff", textAlign: "right" };
const TD = { padding: "9px 6px", fontSize: 10, textAlign: "right", borderBottom: "1px solid #e2e8f0", fontVariantNumeric: "tabular-nums" };

function ItemsTable({ inv }) {
  const cur = inv.currency || "EUR";
  return (
    <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 20 }} data-testid="invoice-items">
      <thead>
        <tr style={{ background: ACCENT }}>
          <th style={{ ...TH, textAlign: "left", width: 28 }}>R.br.</th>
          <th style={{ ...TH, textAlign: "left" }}>Usluga</th>
          <th style={TH}>Kol.</th>
          <th style={TH}>Jed. cijena</th>
          <th style={TH}>Popust</th>
          <th style={TH}>Cijena s popustom</th>
          <th style={TH}>PDV</th>
          <th style={TH}>Sveukupno</th>
        </tr>
      </thead>
      <tbody>
        {(inv.items || []).map((it, i) => (
          <tr key={i} data-testid={`invoice-item-${i}`}>
            <td style={{ ...TD, textAlign: "left" }}>{i + 1}.</td>
            <td style={{ ...TD, textAlign: "left", color: "#0f172a" }}>{it.description}</td>
            <td style={TD}>{Number(it.quantity || 0).toLocaleString("hr-HR")}</td>
            <td style={TD}>{money(it.unit_price, cur)}</td>
            <td style={TD} data-testid={`invoice-item-discount-${i}`}>{Number(it.discount_percent || 0).toLocaleString("hr-HR", { maximumFractionDigits: 2 })} %</td>
            <td style={TD}>{money(it.price_after_discount ?? it.unit_price, cur)}</td>
            <td style={TD}>{Number(it.vat_rate || 0)} %</td>
            <td style={{ ...TD, fontWeight: 700, color: "#0f172a" }}>{money(it.total, cur)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function TotalRow({ label, value, strong, tid }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: strong ? "9px 12px" : "4px 12px", fontSize: strong ? 13 : 10.5, fontWeight: strong ? 800 : 500, background: strong ? ACCENT : "transparent", color: strong ? "#fff" : "#334155" }} data-testid={tid}>
      <span>{label}</span><span style={{ fontVariantNumeric: "tabular-nums" }}>{value}</span>
    </div>
  );
}

function Totals({ inv }) {
  const cur = inv.currency || "EUR";
  const vat = Number(inv.vat_total || 0);
  return (
    <div style={{ marginLeft: "auto", width: 300, marginTop: 16 }}>
      <TotalRow label="Iznos bez popusta" value={money(inv.subtotal, cur)} tid="inv-sum-subtotal" />
      <TotalRow label="Popust" value={`- ${money(inv.discount_total, cur)}`} tid="inv-sum-discount" />
      <TotalRow label="Ukupno s popustom" value={money(inv.total, cur)} tid="inv-sum-after-discount" />
      {vat > 0 ? (
        <>
          <TotalRow label="Osnovica" value={money(Number(inv.total || 0) - vat, cur)} tid="inv-sum-base" />
          <TotalRow label="PDV" value={money(vat, cur)} tid="inv-sum-vat" />
        </>
      ) : (
        <TotalRow label="Oslobođeno PDV-a" value={money(inv.total, cur)} tid="inv-sum-vat-exempt" />
      )}
      <div style={{ marginTop: 6 }}><TotalRow label={`UKUPNO ${cur}`} value={money(inv.total, cur)} strong tid="inv-total" /></div>
    </div>
  );
}

function LegalBox({ inv, editable, onChange }) {
  const missing = !(inv.legal_text || "").trim();
  return (
    <div style={{ flex: 1, border: `1px solid ${editable && missing ? "#e11d48" : "#cbd5e1"}`, borderRadius: 4, padding: "10px 12px", fontSize: 10, color: "#334155" }} data-testid="invoice-legal-box">
      {editable ? (
        <>
          <Lbl>Tekst o PDV-u (obavezno)</Lbl>
          <textarea rows={2} value={inv.legal_text || ""} onChange={(e) => onChange({ legal_text: e.target.value })}
            className="mt-1 w-full resize-y rounded border border-slate-200 bg-white px-2 py-1 text-[11px]" data-testid="inv-legal-text" />
        </>
      ) : (
        <div style={{ whiteSpace: "pre-wrap" }}>{inv.legal_text}</div>
      )}
    </div>
  );
}

function Notes({ inv, editable, onChange }) {
  if (!editable && !inv.notes && !inv.footer_text && !inv.supplier?.issued_by_name) return null;
  return (
    <div style={{ marginTop: 16, fontSize: 10, color: "#475569" }}>
      {editable ? (
        <>
          <Lbl>Napomena</Lbl>
          <textarea rows={2} value={inv.notes || ""} onChange={(e) => onChange({ notes: e.target.value })}
            className="mt-1 w-full rounded border border-slate-200 bg-white px-2 py-1 text-[11px]" data-testid="inv-notes" />
        </>
      ) : inv.notes && <div style={{ whiteSpace: "pre-wrap" }}>{inv.notes}</div>}
      {inv.footer_text && <div style={{ marginTop: 6, whiteSpace: "pre-wrap", fontStyle: "italic" }}>{inv.footer_text}</div>}
      {inv.supplier?.issued_by_name && <div style={{ marginTop: 10 }}>Račun izradio: {inv.supplier.issued_by_name}</div>}
    </div>
  );
}

export default function InvoiceDocument({ inv, editable = false, onChange = () => {} }) {
  if (!inv) return null;
  const issued = inv.status === "issued";
  return (
    <div style={{ "--inv-accent": inv.theme_color || "#0f3d4c", fontFamily: "'IBM Plex Sans', 'Helvetica Neue', Arial, sans-serif", color: "#0f172a", background: "#fff" }} data-testid="invoice-document">
      <Header inv={inv} />
      <div style={{ height: 2, background: ACCENT, margin: "20px 0 18px" }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 32 }}>
        <MetaBlock inv={inv} editable={editable} onChange={onChange} />
        <BuyerBlock inv={inv} editable={editable} onChange={onChange} />
      </div>
      <h2 style={{ marginTop: 26, fontSize: 15, fontWeight: 800, letterSpacing: "0.04em", color: ACCENT }} data-testid="invoice-header-title">
        {TITLES[inv.invoice_type] || TITLES.racun} {issued ? `${/^br\.?\s/i.test(inv.number || "") ? "" : "br. "}${inv.number}` : <span style={{ color: "#d97706", fontSize: 11 }}>(NACRT)</span>}
      </h2>
      {inv.header_text && <div style={{ fontSize: 10, color: "#475569", whiteSpace: "pre-wrap" }}>{inv.header_text}</div>}
      {inv.storno_of_number && <div style={{ fontSize: 10, color: "#e11d48", fontWeight: 600 }}>Storno računa br. {inv.storno_of_number}</div>}
      <ItemsTable inv={inv} />
      <Totals inv={inv} />
      <div style={{ display: "flex", gap: 20, alignItems: "flex-start", marginTop: 26 }}>
        <LegalBox inv={inv} editable={editable} onChange={onChange} />
        {inv.show_payment_info && !["ponuda", "storno"].includes(inv.invoice_type) && <PaymentInfo inv={inv} />}
      </div>
      <Notes inv={inv} editable={editable} onChange={onChange} />
      {inv.cancelled && <div style={{ marginTop: 8, fontSize: 10, color: "#e11d48", fontStyle: "italic" }}>Ovaj račun je storniran.</div>}
    </div>
  );
}
